<template>
  <!-- The standalone /login page now renders the same OnBoarding components
       used by the modal flow. Same design language, same behaviour, same
       error/loading state. The only difference is the chrome — a card on a
       page instead of a Bootstrap modal.
       `purchasing-container--v2` opts in to the redesigned purchases
       stylesheet so the page picks up the same inputs, buttons, surfaces
       and palette as the post-payment claim page. -->
  <div class="login-page purchasing-container--v2">
    <div class="login-card">
      <!-- Left column: imagery + goal-aware copy. Mirrors the modal layout. -->
      <div
        class="left-col"
        :style="{ backgroundImage: 'url(' + backgroundImage + ')' }"
      >
        <div class="left-col-scrim"></div>
        <div class="left-content">
          <OnBoardingDetailPage />
        </div>
      </div>

      <!-- Right column: auth panel. Topographic motif behind the form to
           give the dark surface some texture without warming the palette. -->
      <div class="right-col">
        <div class="topo-motif" aria-hidden="true"></div>
        <div class="right-content">
          <template v-if="isLoading && !currentPageIsForm">
            <div class="redirect-state">
              <span class="spinner"></span>
              <p>{{ $t('login.app.signing_you_in') }}</p>
            </div>
          </template>
          <template v-else>
            <OnBoardingPageRouter />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { OnBoardingGoal, OnBoardingGoalUtility } from '../../utils/models/OnBoardingGoal';
import { OnBoardingPage } from '../../utils/models/OnBoardingPage';
import OnBoardingDetailPage from '../on_boarding/components/OnBoardingDetailPage.vue';
import OnBoardingPageRouter from '../on_boarding/components/OnBoardingPageRouter.vue';
import { auth, onAuthStateChanged } from '../../utils/firebase/auth';
import { isSignInWithEmailLink } from 'firebase/auth';

export default defineComponent({
  name: 'LoginApp',
  components: {
    OnBoardingDetailPage,
    OnBoardingPageRouter,
  },
  data() {
    return {
      authListenerUnsubscribe: null as null | (() => void),
    };
  },
  computed: {
    onBoardingGoalUtility() {
      return new OnBoardingGoalUtility(this.$store.state.onBoarding.onBoardingGoal);
    },
    topPage() {
      return this.$store.getters['onBoarding/lastPage'];
    },
    backgroundImage(): string | undefined {
      return this.onBoardingGoalUtility.image(this.topPage);
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    currentPageIsForm(): boolean {
      // While the user is interacting with a form, keep showing it under the
      // overlay rather than swapping in the "redirecting…" state.
      return this.topPage === OnBoardingPage.EmailLogin
        || this.topPage === OnBoardingPage.EmailSignUp
        || this.topPage === OnBoardingPage.MagicLink;
    },
  },
  created() {
    // Set initial store state BEFORE first render so the goal-aware copy and
    // imagery on the left column don't flash blank on mount.
    this.$store.commit('onBoarding/setStandaloneMode', true);
    this.$store.commit('onBoarding/setOnBoardingGoal', OnBoardingGoal.CreateAccount);
    // Email-link callback: skip the SignUp landing page and route straight
    // into the MagicLink view, which renders the verifying / re-enter-email
    // state while signInWithEmailLink runs.
    if (isSignInWithEmailLink(auth, window.location.href)) {
      this.$store.commit('onBoarding/setPageStack', [OnBoardingPage.MagicLink]);
    } else {
      this.$store.commit('onBoarding/setPageStack', [OnBoardingPage.SignUpOptions]);
    }
  },
  mounted() {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      // Completion action handles its own post-sign-in redirect via
      // setNextPostLoginPage, so we skip the generic onAuthStateChanged
      // listener below — otherwise the two would race to navigate away.
      this.$store.dispatch('onBoarding/completeMagicLinkSignIn');
      return;
    }
    // If the user lands on /login already authenticated, bounce them through
    // the same /firebase/auth handshake the old page used. Preserves the
    // existing redirect-after-login behaviour.
    this.authListenerUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !this.$store.state.onBoarding.user) {
        user.getIdToken().then((token) => {
          window.location.assign(`/firebase/auth?accessToken=${token}`);
        });
      }
    });
  },
  beforeUnmount() {
    if (this.authListenerUnsubscribe) {
      this.authListenerUnsubscribe();
    }
    this.$store.commit('onBoarding/setStandaloneMode', false);
  },
});
</script>

<style scoped>
.login-page {
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

/* Surface gradient + radius matches `pro-claim-card` so the standalone
   /login page reads as a sibling of the post-payment claim panel. */
.login-card {
  display: flex;
  width: 100%;
  max-width: 1100px;
  min-height: 70vh;
  background: linear-gradient(180deg, #1c1d24 0%, #22242c 100%);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}

.left-col {
  position: relative;
  flex: 1 1 50%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  display: flex;
  align-items: center;
  padding: 3rem 2.5rem;
  min-height: 320px;
}
.left-col-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(15, 17, 22, 0.55) 0%, rgba(15, 17, 22, 0.15) 60%);
}
.left-content {
  position: relative;
  z-index: 1;
  width: 100%;
}

.right-col {
  position: relative;
  flex: 1 1 50%;
  background-color: #1a1c22;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 2.5rem;
  overflow: hidden;
}
.topo-motif {
  position: absolute;
  inset: 0;
  background-image: url('../../images/map-lines.webp');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 140% auto;
  opacity: 0.06;
  pointer-events: none;
  z-index: 0;
}
.right-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 380px;
}

.redirect-state {
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
}
.redirect-state .spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.18);
  border-top-color: var(--yellow, #fddf00);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 0.75rem;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 820px) {
  .login-card {
    flex-direction: column;
    min-height: auto;
  }
  .left-col {
    min-height: 220px;
    padding: 2rem 1.5rem;
  }
  .right-col {
    padding: 2rem 1.5rem;
  }
}
</style>
